import { DateInput } from '@mantine/dates';
import { MultiSelect, Select, TextInput, Title, Grid, Box, Group, Badge, Textarea, Alert, Loader } from '@mantine/core';
import { IconCalendar, IconUsers, IconFileReport, IconNote, IconAlertCircle } from '@tabler/icons-react';
import { BasicInfoProps } from './types';
import { useState, useEffect, useRef } from 'react';
import {
  fetchDirektionen,
  fetchDienststellen,
  fetchBereiche,
  getBereichAdresse
} from '../../lib/pocketbase';
import { useRetryOperation } from '../../hooks/useRetryOperation';
import type { SelectOption } from '../../lib/pocketbase';

export function BasicInfo({ form, auditoren, dienststellen: initialDienststellen, auditnummer }: BasicInfoProps) {
  const retry = useRetryOperation();

  const [direktionen, setDirektionen] = useState<SelectOption[]>([]);
  const [dienststellen, setDienststellen] = useState<SelectOption[]>([]);
  const [bereiche, setBereiche] = useState<SelectOption[]>([]);
  const [adresseText, setAdresseText] = useState<string>('');
  
  // Refs to prevent loading multiple times
  const direktionenLoaded = useRef(false);
  const loadedDirektionId = useRef<string | undefined>(undefined);
  const loadedDienststelleId = useRef<string | undefined>(undefined);
  const loadedBereichId = useRef<string | undefined>(undefined);
  
  // Track if we're in the initial population phase
  const isInitializing = useRef(true);
  
  // Loading states
  const [loadingDirektionen, setLoadingDirektionen] = useState(true);
  const [loadingDienststellen, setLoadingDienststellen] = useState(false);
  const [loadingBereiche, setLoadingBereiche] = useState(false);
  const [loadingAdresse, setLoadingAdresse] = useState(false);
  
  // Error states
  const [direktionError, setDirektionError] = useState<string | null>(null);
  const [dienststellenError, setDienststellenError] = useState<string | null>(null);
  const [bereicheError, setBereicheError] = useState<string | null>(null);

  // Initial load: Direktionen
  useEffect(() => {
    let isMounted = true;

    const loadDirektionen = async () => {
      // Prevent multiple loads
      if (direktionenLoaded.current) {
        return;
      }

      setLoadingDirektionen(true);
      setDirektionError(null);
      
      try {
        console.log('[BasicInfo] Loading Direktionen...');
        const list = await retry(() => fetchDirektionen());
        console.log('[BasicInfo] Direktionen loaded:', list.length, 'items');
        
        if (isMounted) {
          setDirektionen(list);
          direktionenLoaded.current = true;
          if (list.length === 0) {
            setDirektionError('Keine Direktionen gefunden');
          }
        }
      } catch (error: any) {
        console.error('[BasicInfo] Error loading Direktionen:', error);
        if (isMounted) {
          setDirektionen([]);
          setDirektionError('Fehler beim Laden der Direktionen: ' + error.message);
        }
      } finally {
        if (isMounted) {
          setLoadingDirektionen(false);
        }
      }
    };

    loadDirektionen();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once

  useEffect(() => {
    // Wait until initial form data has been populated (after edits load existing values)
    // Once direktion/dienststelle/bereich are set at least once, consider initialization done
    if (
      isInitializing.current &&
      (form.values.direktion || form.values.dienststelle || form.values.bereich)
    ) {
      isInitializing.current = false;
    }
  }, [form.values.direktion, form.values.dienststelle, form.values.bereich]);

  // Direktion → Dienststellen
  useEffect(() => {
    const direktionId = form.values.direktion ?? undefined;
    
    if (!direktionId) {
      setDienststellen([]);
      setBereiche([]);
      setAdresseText('');
      setDienststellenError(null);
      const previouslyLoaded = loadedDirektionId.current;
      loadedDirektionId.current = undefined;
      // Clear dependent form values (only if not initializing and we actually had a value)
      if (!isInitializing.current && previouslyLoaded) {
        console.log('[BasicInfo] Clearing dependent fields because Direktion wurde entfernt', {
          previouslyLoaded,
          isInitializing: isInitializing.current
        });
        form.setFieldValue('dienststelle', '');
        form.setFieldValue('bereich', '');
      }
      return;
    }

    // Only clear if we're switching from one direktion to another (not initial load)
    if (!isInitializing.current && loadedDirektionId.current && loadedDirektionId.current !== direktionId) {
      console.log('[BasicInfo] Direktion geändert, räume abhängige Felder', {
        previous: loadedDirektionId.current,
        next: direktionId
      });
      form.setFieldValue('dienststelle', '');
      form.setFieldValue('bereich', '');
    }

    // Prevent loading if already loaded for this direktionId
    if (loadedDirektionId.current === direktionId) {
      return;
    }

    setLoadingDienststellen(true);
    setDienststellenError(null);
    
    console.log('[BasicInfo] Loading Dienststellen for Direktion:', direktionId);
    
    retry(() => fetchDienststellen(direktionId))
      .then((list) => {
        console.log('[BasicInfo] Dienststellen loaded:', list.length, 'items');
        setDienststellen(list);
        loadedDirektionId.current = direktionId;
        if (list.length === 0) {
          setDienststellenError('Keine Dienststellen für diese Direktion gefunden');
        }
      })
      .catch((error: any) => {
        console.error('[BasicInfo] Error loading Dienststellen:', error);
        setDienststellen([]);
        setDienststellenError('Fehler beim Laden der Dienststellen: ' + error.message);
      })
      .finally(() => {
        setLoadingDienststellen(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.direktion]);

  // Dienststelle → Bereiche
  useEffect(() => {
    const dienststelleId = form.values.dienststelle ?? undefined;
    
    if (!dienststelleId) {
      setBereiche([]);
      setAdresseText('');
      setBereicheError(null);
      const previouslyLoaded = loadedDienststelleId.current;
      loadedDienststelleId.current = undefined;
      // Clear dependent form values (only if we had a dienststelle loaded before)
      if (!isInitializing.current && previouslyLoaded) {
        console.log('[BasicInfo] Dienststelle entfernt, resette Bereich/Adresse', {
          previouslyLoaded,
          isInitializing: isInitializing.current
        });
        form.setFieldValue('bereich', '');
      }
      return;
    }

    // Only clear if we're switching from one dienststelle to another (not initial load)
    if (!isInitializing.current && loadedDienststelleId.current && loadedDienststelleId.current !== dienststelleId) {
      console.log('[BasicInfo] Dienststelle geändert, räume Bereich/Adresse', {
        previous: loadedDienststelleId.current,
        next: dienststelleId
      });
      form.setFieldValue('bereich', '');
    }

    // Prevent loading if already loaded for this dienststelleId
    if (loadedDienststelleId.current === dienststelleId) {
      return;
    }

    setLoadingBereiche(true);
    setBereicheError(null);
    
    console.log('[BasicInfo] Loading Bereiche for Dienststelle:', dienststelleId);
    
    retry(() => fetchBereiche(dienststelleId))
      .then((list) => {
        console.log('[BasicInfo] Bereiche loaded:', list.length, 'items');
        setBereiche(list);
        loadedDienststelleId.current = dienststelleId;
        if (list.length === 0) {
          setBereicheError('Keine Bereiche für diese Dienststelle gefunden');
        }
      })
      .catch((error: any) => {
        console.error('[BasicInfo] Error loading Bereiche:', error);
        setBereiche([]);
        setBereicheError('Fehler beim Laden der Bereiche: ' + error.message);
      })
      .finally(() => {
        setLoadingBereiche(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.dienststelle]);

  // Bereich → Adresse automatisch laden und ins Formular übernehmen
  useEffect(() => {
    const bereichId = form.values.bereich ?? undefined;
    
    if (!bereichId) {
      setAdresseText('');
      form.setFieldValue('adresse', '');
      loadedBereichId.current = undefined;
      return;
    }

    // Prevent loading if already loaded for this bereichId
    if (loadedBereichId.current === bereichId) {
      return;
    }

    setLoadingAdresse(true);
    
    console.log('[BasicInfo] Loading Adresse for Bereich:', bereichId);
    
    retry(() => getBereichAdresse(bereichId))
      .then((adresse) => {
        console.log('[BasicInfo] Adresse loaded:', adresse);
        // Setze die Adresse sowohl im lokalen State als auch im Formular
        setAdresseText(adresse);
        form.setFieldValue('adresse', adresse);
        loadedBereichId.current = bereichId;
      })
      .catch((error: any) => {
        console.error('[BasicInfo] Error loading Adresse:', error);
        setAdresseText('');
        form.setFieldValue('adresse', '');
      })
      .finally(() => {
        setLoadingAdresse(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.bereich]);

  return (
    <Box>
      <Title order={3} mb="md" display="flex" style={{ alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
        <Group>
          <IconUsers size={20} />
          <span>Grundinformationen</span>
        </Group>
        {auditnummer && (
          <Badge 
            size="lg"
            variant="light"
            color="gray"
            leftSection={<IconFileReport size={16} style={{ marginRight: -4 }} />}
          >
            Audit #{auditnummer}
          </Badge>
        )}
      </Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <DateInput
            label="Datum"
            placeholder="Wählen Sie ein Datum"
            leftSection={<IconCalendar size={16} />}
            {...form.getInputProps('auditDatum')}
            required
            withAsterisk
            valueFormat="DD.MM.YYYY"
            locale="de"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <MultiSelect
            label="Auditoren"
            placeholder="Wählen Sie Auditoren"
            data={auditoren}
            leftSection={<IconUsers size={16} />}
            searchable
            clearable
            {...form.getInputProps('auditor')}
            required
            withAsterisk
          />
        </Grid.Col>
      </Grid>

      <Box my="md">
        <TextInput
          label="Begleitpersonen"
          placeholder="Namen der Begleitpersonen"
          leftSection={<IconUsers size={16} />}
          {...form.getInputProps('begleitpersonen')}
        />
      </Box>

      <Grid>
        <Grid.Col span={{ base: 12 }}>
          {direktionError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="xs">
              {direktionError}
            </Alert>
          )}
          <Select
            label="Direktion"
            placeholder={loadingDirektionen ? 'Lade Direktionen...' : 'Bitte Direktion wählen'}
            data={direktionen}
            searchable
            clearable
            rightSection={loadingDirektionen && <Loader size={16} />}
            {...form.getInputProps('direktion')}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12 }}>
          {dienststellenError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="xs">
              {dienststellenError}
            </Alert>
          )}
          <Select
            label="Dienststelle"
            placeholder={
              !form.values.direktion 
                ? 'Bitte zuerst eine Direktion wählen' 
                : loadingDienststellen 
                  ? 'Lade Dienststellen...' 
                  : 'Bitte Dienststelle wählen'
            }
            data={dienststellen}
            searchable
            clearable
            disabled={!form.values.direktion || loadingDienststellen}
            rightSection={loadingDienststellen && <Loader size={16} />}
            {...form.getInputProps('dienststelle')}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12 }}>
          {bereicheError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="xs">
              {bereicheError}
            </Alert>
          )}
          <Select
            label="Bereich / Abteilung"
            placeholder={
              !form.values.dienststelle 
                ? 'Bitte zuerst eine Dienststelle wählen' 
                : loadingBereiche 
                  ? 'Lade Bereiche...' 
                  : 'Bitte Bereich wählen'
            }
            data={bereiche}
            searchable
            clearable
            disabled={!form.values.dienststelle || loadingBereiche}
            rightSection={loadingBereiche && <Loader size={16} />}
            {...form.getInputProps('bereich')}
          />
        </Grid.Col>

      </Grid>

      <Box my="md">
        <Textarea
          label="Bemerkungen"
          placeholder="Zusätzliche Anmerkungen zum Audit"
          minRows={3}
          autosize
          leftSection={<IconNote size={16} />}
          {...form.getInputProps('bemerkungen')}
        />
      </Box>
    </Box>
  );
}
